import { ShieldCheck, IdCard, Lock, Award, Star, CheckCircle2, Sparkles } from "lucide-react";

/**
 * Trust Score card — surfaces a 0-100 score + 6-category breakdown.
 * `trust` is the object returned by the backend: { total, tier, breakdown: {...} }
 */
const TIER_CONFIG = {
  elite: {
    label: "Confianza Elite",
    ring: "from-emerald-500 to-emerald-300",
    text: "text-emerald-700",
    bg: "bg-emerald-50",
    border: "border-emerald-200",
  },
  high: {
    label: "Confianza Alta",
    ring: "from-emerald-500 to-teal-300",
    text: "text-emerald-700",
    bg: "bg-emerald-50",
    border: "border-emerald-200",
  },
  medium: {
    label: "Confianza Media",
    ring: "from-amber-500 to-amber-300",
    text: "text-amber-700",
    bg: "bg-amber-50",
    border: "border-amber-200",
  },
  low: {
    label: "Confianza Baja",
    ring: "from-orange-500 to-orange-300",
    text: "text-orange-700",
    bg: "bg-orange-50",
    border: "border-orange-200",
  },
  new: {
    label: "Cuenta nueva",
    ring: "from-neutral-400 to-neutral-300",
    text: "text-neutral-600",
    bg: "bg-neutral-100",
    border: "border-neutral-200",
  },
};

const CATEGORY_META = [
  { key: "identity", label: "Identidad", Icon: IdCard, color: "emerald" },
  { key: "security", label: "Seguridad", Icon: Lock, color: "blue" },
  { key: "activity", label: "Actividad", Icon: Sparkles, color: "violet" },
  { key: "rating", label: "Calificación", Icon: Star, color: "amber" },
  { key: "reliability", label: "Confiabilidad", Icon: CheckCircle2, color: "teal" },
  { key: "reputation", label: "Reputación", Icon: ShieldCheck, color: "rose" },
];

function Ring({ value, max = 100, tierCfg }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  // SVG circle: r=46, circumference ≈ 289.027
  const C = 2 * Math.PI * 46;
  const offset = C * (1 - pct / 100);
  return (
    <div className="relative w-32 h-32 flex items-center justify-center flex-shrink-0">
      <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 100 100">
        <defs>
          <linearGradient id="ts-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" className={`stop-${tierCfg.ring.split(" ")[0].replace("from-", "")}`} style={{ stopColor: "currentColor" }} />
            <stop offset="100%" className={`stop-${tierCfg.ring.split(" ")[1].replace("to-", "")}`} style={{ stopColor: "currentColor" }} />
          </linearGradient>
        </defs>
        <circle cx="50" cy="50" r="46" fill="none" stroke="#E5E7EB" strokeWidth="6" />
        <circle
          cx="50"
          cy="50"
          r="46"
          fill="none"
          stroke="currentColor"
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={C}
          strokeDashoffset={offset}
          className={`${tierCfg.text} transition-all duration-700`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-3xl font-extrabold ${tierCfg.text}`}>{Math.round(value)}</span>
        <span className="text-[10px] uppercase tracking-wider text-neutral-500">de 100</span>
      </div>
    </div>
  );
}

function CategoryRow({ label, Icon, score, max }) {
  const pct = Math.max(0, Math.min(100, (score / max) * 100));
  return (
    <div className="flex items-center gap-3" data-testid={`trust-row-${label.toLowerCase()}`}>
      <div className="w-8 h-8 rounded-xl bg-neutral-100 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-neutral-700" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-agave">{label}</span>
          <span className="text-xs text-neutral-500 tabular-nums">
            {score.toFixed(1)} / {max}
          </span>
        </div>
        <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all duration-700"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    </div>
  );
}

export default function TrustScoreCard({ trust, compact = false, testid = "trust-score-card" }) {
  if (!trust || typeof trust.total !== "number") return null;
  const tierCfg = TIER_CONFIG[trust.tier] || TIER_CONFIG.new;

  if (compact) {
    // Inline hero badge used on profile heros and admin tables.
    return (
      <div
        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 ${tierCfg.bg} ${tierCfg.border}`}
        data-testid={testid}
      >
        <Award className={`w-4 h-4 ${tierCfg.text}`} />
        <span className={`text-sm font-semibold ${tierCfg.text}`}>
          {Math.round(trust.total)}
        </span>
        <span className="text-xs text-neutral-600">· {tierCfg.label}</span>
      </div>
    );
  }

  return (
    <div className="card-base" data-testid={testid}>
      <div className="flex items-start gap-5 flex-col sm:flex-row">
        <Ring value={trust.total} tierCfg={tierCfg} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Award className={`w-5 h-5 ${tierCfg.text}`} />
            <h3 className="font-heading font-semibold text-lg text-agave">
              {tierCfg.label}
            </h3>
          </div>
          <p className="text-sm text-neutral-600 mb-4">
            Tu puntuación de confianza combina identidad, seguridad, actividad,
            calificaciones y reputación.
          </p>
          <div className="space-y-3">
            {CATEGORY_META.map(({ key, label, Icon }) => {
              const row = trust.breakdown?.[key];
              if (!row) return null;
              return (
                <CategoryRow
                  key={key}
                  label={label}
                  Icon={Icon}
                  score={Number(row.score) || 0}
                  max={Number(row.max) || 0}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
